import React, { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import MonitorStats from "../components/MonitorStats";
import MonitorsTable from "../components/MonitorsTable";
import EditMonitorModal from "../components/EditMonitorModal";
import WhitelistModal from "../components/WhitelistModal";
import BulkWebhookModal from "../components/BulkWebhookModal";
import { Col, Row, Button } from "react-bootstrap";
import { FiSettings, FiLink, FiX } from "react-icons/fi";
import { useOutletContext } from "react-router-dom";
import { getHost } from "../utils/utils";
import BlacklistModal from "../components/BlacklistModal";
import CategoryDefaultsModal from "../components/CategoryDefaultsModal";

// Client ID that has permission to edit regions and categories
const ADMIN_CLIENT_ID = "68fe273606016adb4b0c5904";

const API_MONITORS = `${getHost()}/api/monitors`;
const API_CLIENTS = `${getHost()}/api/clients`;
const getToken = () => localStorage.getItem("jwt");

const Monitors = () => {
  const { toggleSidebar } = useOutletContext();
  const [monitors, setMonitors] = useState([]);
  const [allMonitors, setAllMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showWhitelist, setShowWhitelist] = useState(false);
  const [showBlacklist, setShowBlacklist] = useState(false);
  const [showBulkWebhook, setShowBulkWebhook] = useState(false);
  const [showCategoryDefaults, setShowCategoryDefaults] = useState(false);
  const [selectedMonitors, setSelectedMonitors] = useState(new Set());
  const [clients, setClients] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All Packages");
  const [selectedRegion, setSelectedRegion] = useState("All Regions");
  const [canEditRegion, setCanEditRegion] = useState(false);
  const [canEditCategory, setCanEditCategory] = useState(false);

  const fetchMonitors = useCallback((category) => {
    setLoading(true);

    const url =
      category === "Not Set" ? `${API_MONITORS}?filter=not_set` : API_MONITORS;

    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        const fetchedMonitors = data.monitors || [];

        if (category === "All Packages" || category === "Not Set") {
          setMonitors(fetchedMonitors);
          if (category === "All Packages") {
            setAllMonitors(fetchedMonitors);
          }
        } else {
          setMonitors((prevAllMonitors) => {
            return prevAllMonitors.filter((m) => m.category === category);
          });
        }

        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchMonitors("All Packages");
  }, [fetchMonitors]);

  // Check if user has permission to edit regions
  useEffect(() => {
    fetch(API_CLIENTS, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => res.json())
      .then((data) => {
        const userClients = data.clients || [];
        // Check if user has access to the admin client
        const hasAdminAccess = userClients.some(
          (client) => client._id === ADMIN_CLIENT_ID
        );
        setCanEditRegion(hasAdminAccess);
        setCanEditCategory(hasAdminAccess);
        setClients(userClients);
      })
      .catch(() => setCanEditRegion(false));
  }, []);

  const handleCategoryChange = useCallback(
    (category) => {
      setSelectedCategory(category);

      if (category === "All Packages") {
        setMonitors(allMonitors);
      } else if (category === "Not Set") {
        fetchMonitors("Not Set");
      } else {
        const filtered = allMonitors.filter((m) => m.category === category);
        setMonitors(filtered);
      }
    },
    [allMonitors, fetchMonitors],
  );

  const refreshClients = useCallback(() => {
    fetch(API_CLIENTS, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((res) => res.json())
      .then((data) => setClients(data.clients || []));
  }, []);

  const openEdit = useCallback((monitor) => {
    setSelectedMonitor(monitor);
    setShowEdit(true);
    refreshClients();
  }, [refreshClients]);

  const openWhitelist = useCallback((monitor) => {
    setSelectedMonitor(monitor);
    setShowWhitelist(true);
    refreshClients();
  }, [refreshClients]);

  const openBlacklist = useCallback((monitor) => {
    setSelectedMonitor(monitor);
    setShowBlacklist(true);
    refreshClients();
  }, [refreshClients]);

  const handleCategoryUpdated = useCallback((monitorId, newCategory) => {
    const updateMonitorCategory = (monitorsList) =>
      monitorsList.map((m) =>
        m._id === monitorId ? { ...m, category: newCategory } : m
      );

    setMonitors(updateMonitorCategory);
    setAllMonitors(updateMonitorCategory);
  }, []);

  const handleRegionUpdated = useCallback((monitorId, newRegion) => {
    // Update the monitor in both lists
    const updateMonitorRegion = (monitorsList) =>
      monitorsList.map((m) =>
        m._id === monitorId ? { ...m, region: newRegion } : m
      );

    setMonitors(updateMonitorRegion);
    setAllMonitors(updateMonitorRegion);
  }, []);

  const categories = [
    "All Packages",
    ...new Set(allMonitors.map((m) => m.category).filter(Boolean)),
    "Not Set",
  ];

  const regions = [
    "All Regions",
    ...new Set(allMonitors.map((m) => m.region).filter(Boolean)).values(),
    "Not Set",
  ].sort((a, b) => {
    // Keep "All Regions" first and "Not Set" last
    if (a === "All Regions") return -1;
    if (b === "All Regions") return 1;
    if (a === "Not Set") return 1;
    if (b === "Not Set") return -1;
    return a.localeCompare(b);
  });

  const clientsFor = (monitor) =>
    monitor?.client_ids ? clients.filter((c) => monitor.client_ids.includes(c._id)) : clients;

  const bulkClients = clients.filter((c) =>
    allMonitors
      .filter((m) => selectedMonitors.has(m._id))
      .every((m) => !m.client_ids || m.client_ids.includes(c._id)),
  );

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiSettings className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Monitor Settings</h4>
              <p className="text-white-65 fs-14 mb-0">
                Manage your monitor settings with an ease
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 mb-4">
        <MonitorStats />
      </div>

      <div className="px-4">
        <MonitorsTable
          monitors={monitors}
          loading={loading}
          onEdit={openEdit}
          onWhitelist={openWhitelist}
          onBlacklist={openBlacklist}
          canEditCategory={canEditCategory}
          onCategoryUpdate={handleCategoryUpdated}
          canEditRegion={canEditRegion}
          onRegionUpdate={handleRegionUpdated}
          selectedCategory={selectedCategory}
          setSelectedCategory={handleCategoryChange}
          categories={categories}
          selectedRegion={selectedRegion}
          setSelectedRegion={setSelectedRegion}
          regions={regions}
          selectedMonitors={selectedMonitors}
          onSelectionChange={setSelectedMonitors}
          onOpenCategoryDefaults={() => setShowCategoryDefaults(true)}
        />
      </div>

      <EditMonitorModal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        monitor={selectedMonitor}
        clients={clientsFor(selectedMonitor)}
      />

      <WhitelistModal
        show={showWhitelist}
        onHide={() => setShowWhitelist(false)}
        monitor={selectedMonitor}
        clients={clientsFor(selectedMonitor)}
      />

      <BlacklistModal
        show={showBlacklist}
        onHide={() => setShowBlacklist(false)}
        monitor={selectedMonitor}
        clients={clientsFor(selectedMonitor)}
      />

      <BulkWebhookModal
        show={showBulkWebhook}
        onHide={() => setShowBulkWebhook(false)}
        monitorIds={selectedMonitors}
        clients={bulkClients}
        onSuccess={() => {
          setShowBulkWebhook(false);
          setSelectedMonitors(new Set());
        }}
      />

      <CategoryDefaultsModal
        show={showCategoryDefaults}
        onHide={() => setShowCategoryDefaults(false)}
        clients={clients}
      />

      {selectedMonitors.size > 0 && (
        <div className="bulk-action-bar d-flex align-items-center gap-3">
          <span className="text-white fw-500 fs-14">
            {selectedMonitors.size} monitor{selectedMonitors.size !== 1 ? "s" : ""} selected
          </span>
          <Button
            variant="primary"
            size="sm"
            className="rounded-3 fw-600 fs-12 d-inline-flex align-items-center gap-1"
            onClick={() => setShowBulkWebhook(true)}
          >
            <FiLink className="fs-14" />
            Add Webhook
          </Button>
          <Button
            variant=""
            size="sm"
            className="text-gray fs-14"
            onClick={() => setSelectedMonitors(new Set())}
            title="Clear selection"
          >
            <FiX />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Monitors;
